import type Contact from "../entities/contact";
import type Role from "../entities/role";
import util from '../lib/util';
import * as DateUtil from '../lib/dateutils';
import newHRTimestamp from '../lib/picos/timestamp';
import type {uuid, SchemaField, EntitySchema} from "../@types/schema";
import Base from "./base";
import {genUUID} from "../lib/picos/uuids";
import type {EntityType} from "../@types/entity";
import {ValidationError} from "../lib/errors";
function getCurrentUserId() { return sessionStorage.getItem('userId'); }
function getCurrentFirmId() { return sessionStorage.getItem('firmId'); }
export const SecurityPolicy = {
    AllowAll: "A",
    DenyAll: "D"
};
export type SecurityPolicyType = typeof SecurityPolicy[keyof typeof SecurityPolicy];
export const BillableHoursUnit = {
    Day: "day",
    Week: "week",
    Month: "month",
    Year: "year"
};
export type BillableHoursUnitType = typeof BillableHoursUnit[keyof typeof BillableHoursUnit];
class User extends Base implements EntityType {
    userId: uuid;
    email: string;
    enabled: boolean;
    contactId?: uuid | null;
    securityPolicy: SecurityPolicyType;
    password?: string | null;
    billableRate: number;
    billableHoursTarget: number;
    billableHoursUnit: BillableHoursUnitType;
    picture?: string | null;
    ssn?: string | null;
    dob?: Date | null;
    startDate?: Date | null;
    endDate?: Date | null;
    contact?: Contact | null;
    roles: Role[];
    lfm?: string | null;
    lfmComputed?: number | null;
    fml?: string | null;
    fmlComputed?: number | null;
    shorten?: string | null;
    initials?: string | null;
    constructor(doc: typeof User | any) {
        super(doc);
        if (!doc) doc = {
            firmId: getCurrentFirmId(),
            addedBy: getCurrentUserId(),
            addedOn: newHRTimestamp(),
            status: "C",
            entityId: null,
            entityType: null,
            userId: genUUID(),
            email: null,
            securityPolicy: "A",
        };
        // @ts-ignore
        this.entityId = doc._entityId || doc.entityId || doc.entityid || '00000000-0000-0000-0000-000000000000';
        // @ts-ignore
        this.entityType = doc._entityType || doc.entityType || doc.entitytype || "user";
        // @ts-ignore
        this.userId = doc._userId || doc.userId || doc.userid || genUUID();
        // @ts-ignore
        this.email = doc._email || doc.email || null;
        // @ts-ignore
        this.enabled = util.defaultBoolean([doc.enabled, doc._enabled, doc.enabled, true]);
        // @ts-ignore
        this.contactId = doc._contactId || doc.contactId || doc.contactid || null;
        // @ts-ignore
        this.securityPolicy = util.trimRight(doc._securityPolicy) || util.trimRight(doc.securityPolicy) || util.trimRight(doc.securitypolicy) || "A";
        // @ts-ignore
        this.password = doc._password || doc.password || null;
        // @ts-ignore
        this.billableRate = util.coalesce(util.parseFloat(doc._billableRate), util.parseFloat(doc.billableRate), util.parseFloat(doc.billablerate), 0);
        // @ts-ignore
        this.billableHoursTarget = util.coalesce(util.parseFloat(doc._billableHoursTarget), util.parseFloat(doc.billableHoursTarget), util.parseFloat(doc.billablehourstarget), 20);
        // @ts-ignore
        this.billableHoursUnit = doc._billableHoursUnit || doc.billableHoursUnit || doc.billablehoursunit || "week";
        // @ts-ignore
        this.picture = doc._picture || doc.picture || null;
        // @ts-ignore
        this.ssn = doc._ssn || doc.ssn || null;
        // @ts-ignore
        this.dob = DateUtil.dateOnly(doc._dob) || DateUtil.dateOnly(doc.dob) || null;
        // @ts-ignore
        this.startDate = DateUtil.dateOnly(doc._startDate) || DateUtil.dateOnly(doc.startDate) || DateUtil.dateOnly(doc.startdate) || null;
        // @ts-ignore
        this.endDate = DateUtil.dateOnly(doc._endDate) || DateUtil.dateOnly(doc.endDate) || DateUtil.dateOnly(doc.enddate) || null;
        // @ts-ignore
        this.contact = doc._contact || doc.contact || {};
        // @ts-ignore
        this.roles = doc._roles || doc.roles || [];
        // @ts-ignore
        this.lfm = doc._lfm || doc.lfm || null;
        // @ts-ignore
        this.lfmComputed = util.coalesce(util.parseInt(doc._lfmComputed), util.parseInt(doc.lfmComputed), util.parseInt(doc.lfmcomputed), null);
        // @ts-ignore
        this.fml = doc._fml || doc.fml || null;
        // @ts-ignore
        this.fmlComputed = util.coalesce(util.parseInt(doc._fmlComputed), util.parseInt(doc.fmlComputed), util.parseInt(doc.fmlcomputed), null);
        // @ts-ignore
        this.shorten = doc._shorten || doc.shorten || null;
        // @ts-ignore
        this.initials = doc._initials || doc.initials || null;
    }
    get id(): uuid { return this.userId; }
    static SECURITY_POLICY_VALUES = ["A", "D"];
    static BILLABLE_HOURS_UNIT_VALUES = ["day", "week", "month", "year"];
    addRole(role: Role): Role {
        if (!role) throw new ValidationError('No nulls allowed in an array!');
        if (!this.roles) this.roles = [];
        this.roles.push(role);
        return role;
    }
    updateRole(role: Role) {
        if (!this.roles) this.roles = [];
        const index = this.roles.findIndex(item => item === role);
        if (index !== -1) { this.roles[index] = role; }
        else this.addRole(role);
    }
    removeRole(role: Role) {
        if (!this.roles) return;
        let foundItems = this.roles?.filter(item => item === role);
        if (foundItems && foundItems.length > 0) {
            this.roles = this.roles.filter(item => !(item === role));
        }
    }
    abandonRoles() { this.roles = []; }
    static override getPkFields(): SchemaField[] { return [{"type":"uuid","required":true,"pk":true,"default":"__UUID__","comment":"Unique ID for user"},{"type":"uuid","required":true,"pk":true,"base":true,"default":"__FIRM__","mergeStrategy":"immutable","comment":"Associated firm"}]; }
    toLowerCase() { return this.userId; }
    static override getNonRefArrayFields(): string[] { return ["roles"]; }
    static override getDefaultEntries(): User[] {
        return [];
    }
    static override getSchema(): EntitySchema {
        return Base.withBaseFields({
    entity: 'user',
    table: 'USERS',
    // Keep deleted rows in the client store and backup (historical task assignments, rates). OWA-68
    includeDeletedInResults: true,
    comment: `A staff member of a law firm who has login access to Outlaw Practice. Tracks the person's email (used as login), enabled/security-policy access controls, default billable rate and hours target, employment start and end dates with the firm, and encrypted personal details (SSN, date of birth). Each user optionally links to a Contact record for name and contact information, and carries assigned roles that govern permissions.`,
    fields: {
        userId: {
            type: 'uuid', required: true, pk: true, default: '__UUID__', comment: 'Unique ID for user'
        },
        email: {
            type: 'string', required: true, maxLength: 128, unique: true, comment: `Email is the user's login`,

        },
        enabled: {
            type: 'boolean', default: true, comment: 'Is the user enabled?'
        },
        contactId: {
            type: 'uuid', entityType: 'contact', comment: 'Associated contact, not required but recommended'
        },
        securityPolicy: {
            type: 'char', maxLength: 1, values: ['A', 'D'], valueNames: ['Allow All', 'Deny All'],
            default: 'A', required: true, comment: 'Allow-All or Deny-All'
        },
        password: {
            type: 'string', transient: true, maxLength: 128, comment: 'Password'
        },
        billableRate: {
            type: 'money', default: 0, comment: 'Default hourly billable rate'
        },
        billableHoursTarget: {
            type: 'float', default: 20, comment: 'Default billable hours for this user'
        },
        billableHoursUnit: {
            type: 'string', default: 'week', values: ['day', 'week', 'month', 'year'],
            comment: 'Default unit of measurement for billable hours target'
        },
        picture: {
            type: 'string', comment: `The user's avatar reference — either 'avatar:<timestamp>' for an uploaded image or a legacy URL (not the image data itself)`
        },
        ssn: {
            type: 'string', encrypted: true, comment: 'Social Security Number'
        },
        dob: {
            type: 'date', encrypted: true, comment: 'Date of birth'
        },
        startDate: {
            type: 'date', comment: 'Date the user started with the firm'
        },
        endDate: {
            type: 'date', comment: 'Date the user left the firm'
        },

        /* Transient fields and precomputed fields */
        contact: {
            type: 'json', jsdoc: 'Contact', transient: true, comment: 'Contact associated with the user'
        },
        roles: {
            type: 'json[]', jsdoc: 'Role', transient: true, comment: 'Roles associated with the user'
        },

        lfm: {type: 'string', transient: true, computed: true, comment: 'Last First Middle precomputed'},
        lfmComputed: {type: 'int', transient: true, computed: true, comment: 'addedOn for computed LFM'},
        fml: {type: 'string', transient: true, computed: true, comment: 'First Middle Last precomputed'},
        fmlComputed: {type: 'int', transient: true, computed: true, comment: 'addedOn for computed FML'},
        shorten: {type: 'string', transient: true, computed: true, comment: 'shorten precomputed'},
        initials: {type: 'string', transient: true, computed: true, comment: 'initials precomputed'},
    },
    sortOrder: [
        { position: 1, fieldName: 'email', asc: true }
    ],
    hooks: [],
    indexes: [
        {name: 'notDeleted', fn: t => t.status !== 'D'},
    ],
    maps: [
        {name: 'contactId', keysFn: t => t.contactId, presentFn: (t, k) => t.contactId === k},
    ],
    requiredTypes: ['Contact', 'Role']
});
    }
}
export default User;
